import { Modal as ModalModule, Utils } from 'common';
import PropTypes from 'prop-types';
import React from 'react';
import Print from '../shared/Print';
import { RefundType } from '../shared/RefundTypeOptions';
import HistoryCommunication from '../utils/HistoryCommunication';
import HistoryUtil from '../utils/Util';
import CertifiedRefundContent from './CertifiedRefundContent.component';
import MailInRefundRequestModal from './MailInRefundRequestModal.component';
import NetStampsRefundContent from './NetStampsRefundContent.component';
import RefundSubmittedFailureModal from './RefundSubmittedFailureModal.component';

const { Modal, ModalButtonActionType } = ModalModule;
const { Util, withLayoutAndModalContexts, Session, HttpStatusCode } = Utils;

const RefundModalView = {
	NetStamps: 'netStamps',
	Certified: 'certified',
};

class RefundModal extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			// When selected prints contain any Mail-In types, first displays NetStamps Refund Grid view
			refundModalView: '',

			eRefundPrints: [], // all eligible E-Refund prints
			mailInPrints: [], // all eligible MailIn prints
			selectedMailInPrints: [], // selected MailIn (NetStamps) prints

			// All prints that are requested for refund
			selectedPrintsForRefund: [],
			totalCost: 0,
			isLoading: true,
			isRefundSubmitButtonDisabled: false,
		};
	}

	componentDidMount = async () => {
		const { data = {} } = this.props;
		const { eligiblePrints } = data;

		const { eRefundPrints, labelIds, containsNetstamps } =
			eligiblePrints.reduce(
				(_result, print) => {
					const { refundType, labelId, printTypeName } = print.printDetails;

					if (refundType === RefundType.MailIn) {
						if (printTypeName === 'NetStamps') {
							_result.containsNetstamps = true;
						}

						/**
						 * Aggregate the 'universalIdList' data from all Mail-In prints
						 * For example, user selects two Mail-In prints in the grid.
						 * Print 1 has universalIdList="1, 2" and the Print 2 has universalIdList="3, 4".
						 * The final result will be "1, 2, 3, 4" for the second GetPrints call.
						 */
						_result.labelIds = labelId;
						_result.eRefundPrints.push(print);
					} else if (refundType === RefundType.ERefund) {
						_result.eRefundPrints.push(print);
					}

					return _result;
				},
				{
					eRefundPrints: [],
					labelIds: '',
					containsNetstamps: false,
				}
			);

		const hasMailInPrints = labelIds.length > 0;
		const mailInPrints = hasMailInPrints
			? await this.fetchLabelDetails(labelIds)
			: [];

		this.setState({
			// When selected prints contain any Mail-In types, first displays NetStamps Refund Grid view
			// Envelope prints are now Mail-In prints, so skip Netstamps grid view if there aren't any
			refundModalView:
				hasMailInPrints && containsNetstamps
					? RefundModalView.NetStamps
					: RefundModalView.Certified,
			mailInPrints,
			selectedPrintsForRefund: [...eRefundPrints],
			isLoading: false,
		});
	};

	/**
	 * Call getPrintDetails API to return the print details
	 * @return {object}
	 */
	fetchPrintDetails = async params => {
		const {
			modalContext: { showError },
		} = this.props;

		// TODO: Consume GetPrint API when it is ready
		const queryString = HistoryUtil.buildQueryString(params);
		const response = await HistoryCommunication.getPrints(queryString);

		if (!HistoryCommunication.isSuccessful(response)) {
			showError({ response });
		}

		const { data = {} } = response;
		const { prints = [] } = data;

		return prints.reduce((_prints, printData) => {
			const parsedData = Print(printData);

			if (parsedData.universalId !== 0 && parsedData.eligibleForRefund) {
				_prints.push(parsedData);
			}
			return _prints;
		}, []);
	};

	/**
	 * Call getLabelDetails API to return the print details
	 * @return {object}
	 */
	fetchLabelDetails = async labelIds => {
		const {
			modalContext: { showError },
		} = this.props;

		const response = await HistoryCommunication.getLabelDetails(labelIds);

		if (HistoryCommunication.isSuccessful(response)) {
			return Util.toCamelCase(response.data);
		} else {
			showError({ response });
		}
	};

	buildPayload = () => {
		const { selectedPrintsForRefund, selectedMailInPrints } = this.state;
		const labelIDs = [];
		const universalIds = [];

		for (let i = 0; i < selectedPrintsForRefund.length; i++) {
			if (selectedPrintsForRefund.length > 0) {
				const { refundType, labelId, printTypeName } =
					selectedPrintsForRefund[i].printDetails;

				if (
					refundType === RefundType.ERefund ||
					printTypeName !== 'NetStamps'
				) {
					labelIDs.push(labelId);
				}
			}
		}

		for (let i = 0; i < selectedMailInPrints.length; i++) {
			if (selectedMailInPrints.length > 0) {
				let { universalId } = selectedMailInPrints[i];

				if (typeof universalId === 'bigint') {
					universalId = universalId.toString();
				}

				universalIds.push(universalId);
			}
		}

		return {
			label_ids: labelIDs,
			universal_ids: universalIds,
			label_options: {
				label_size: '4x6',
				label_format: 'png',
				label_logo_image_id: 0,
				label_output_type: 'url',
			},
		};
	};

	onCancelClick = () => {
		const {
			modalContext: { closeAll },
		} = this.props;

		closeAll();
	};

	onNetStampsRefundContinueClick = () => {
		const {
			modalContext: { showMessage },
		} = this.props;
		const { eRefundPrints, selectedMailInPrints, selectedPrintsForRefund } =
			this.state;
		const containsEnvelopes = selectedMailInPrints.some(
			({ printDetails = {} }) => printDetails?.printTypeName === 'Envelope'
		);

		// No MailIn (NetStamps) prints are selected for refund
		if (
			selectedMailInPrints.length === 0 &&
			eRefundPrints.length === 0 &&
			!containsEnvelopes
		) {
			return showMessage({
				data: {
					title: 'No NetStamps Selected',
					description: 'You must select at least one NetStamps to continue.',
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Ok',
				},
			});
		}

		this.setState({
			refundModalView: RefundModalView.Certified,

			// Selected Mail-In (NetStamps) and E-Refund prints for refund
			selectedPrintsForRefund:
				selectedMailInPrints.length > 0
					? [...selectedPrintsForRefund, ...eRefundPrints]
					: eRefundPrints,
		});
	};

	onCertifiedRefundSubmitClick = async () => {
		const {
			modalContext: { closeAll, showModal, showMessage, showError },
			data: { onRefreshGridAPI },
		} = this.props;

		const { selectedPrintsForRefund, selectedMailInPrints } = this.state;
		const failedRequestedPrints = [];
		const refundPayload = this.buildPayload();
		let isAnyMailInRefundRequestSuccessful = false;

		this.setState({
			isRefundSubmitButtonDisabled: true,
		});

		const response = await HistoryCommunication.submitRefund(refundPayload);

		const isSuccessful = HistoryCommunication.isSuccessful(response);
		const httpStatus = isSuccessful ? response.httpStatus : null;
		const refundData =
			httpStatus === HttpStatusCode.OK ? Util.toCamelCase(response.data) : {};

		// Note: Even if all refunds fail in a bulk refund, the response will still be success
		if (isSuccessful) {
			for (let i = 0; i < refundData.statuses.length; i++) {
				const { labelId, statusCode, refundType } = refundData.statuses[i];

				if (statusCode !== 'success') {
					const failedRefund = selectedPrintsForRefund.find(
						print => print?.printDetails?.labelId === labelId
					);

					// select the failed refund response and update the statuses array to only include its own data
					const failedResponse = {
						...response,
						data: { statuses: refundData.statuses[i] },
					};

					failedRequestedPrints.push({ ...failedRefund, ...failedResponse });
				} else if (refundType === 'mail-in') {
					isAnyMailInRefundRequestSuccessful = true;
				}
			}
		} else {
			showError({
				response,
			});
		}

		closeAll(() => {
			if (failedRequestedPrints.length > 0) {
				showModal({
					component: RefundSubmittedFailureModal,
					data: {
						failedRequestedPrints,
						isAllRequestedPrintsFailed:
							failedRequestedPrints.length === selectedPrintsForRefund.length,
					},
					closeCallback: () => {
						// show "Mail-In Refund Request" refund modal when at least one
						// Mail-In refund request was successful, otherwise nothing happens
						if (isAnyMailInRefundRequestSuccessful) {
							showModal({
								component: MailInRefundRequestModal,
								data: response.data,
								closeCallback: () => {
									onRefreshGridAPI();
								},
							});
						}
					},
				});
			}
			// all submitted refund requests are e-refund types
			else if (
				selectedMailInPrints.length === 0 &&
				isAnyMailInRefundRequestSuccessful === false
			) {
				showMessage({
					data: {
						title: 'Refund Submitted',
						description: `<p><b>Your refund has been submitted to the carrier.</b></p>
					<p>
						Most carriers process refunds immediately, although the USPS may
						take 2-3 weeks to review requests.
					</p>
					<p>
						We will notify you via email when the carrier has completed their
						review.
					</p>`,
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Ok',
						primaryButtondataqa: 'refund-refund-submitted-ok-button',
					},
					closeCallback: () => {
						onRefreshGridAPI();
					},
				});
			} else if (
				selectedMailInPrints.length > 0 ||
				isAnyMailInRefundRequestSuccessful
			) {
				// some of the refunds are mail-in type
				showModal({
					component: MailInRefundRequestModal,
					data: response.data,
					closeCallback: () => {
						onRefreshGridAPI();
					},
				});
			}
		});

		this.setState({
			isRefundSubmitButtonDisabled: false,
		});
		// update postage balance
		await Session.loadBalance();
	};

	onNetStampsRefundSelectionChanged = selection => {
		const { selectedPrintsForRefund } = this.state;

		const selectedMailInPrints = selection.map(({ data }) => data);

		this.setState({
			selectedMailInPrints,
			selectedPrintsForRefund,
			totalCost: selectedMailInPrints.reduce(
				(total, { amount }) => total + amount,
				0
			),
		});
	};

	render() {
		const { modalId } = this.props;
		const {
			refundModalView,
			selectedPrintsForRefund,
			mailInPrints,
			totalCost,
			isLoading,
			isRefundSubmitButtonDisabled,
		} = this.state;

		const buttons = {
			primaryButton: {
				type: ModalButtonActionType.Ok,
				text:
					refundModalView === RefundModalView.NetStamps ? 'Continue' : 'Submit',
				onClick:
					refundModalView === RefundModalView.NetStamps
						? this.onNetStampsRefundContinueClick
						: this.onCertifiedRefundSubmitClick,
				disabled: isRefundSubmitButtonDisabled,
				dataqa: `refund-${
					refundModalView === RefundModalView.NetStamps ? 'Continue' : 'Submit'
				}-button`,
			},
			secondaryButton: {
				type: ModalButtonActionType.Ok,
				text: 'Cancel',
				onClick: this.onCancelClick,
				dataqa: 'refund-cancel-button',
			},
		};

		return (
			<Modal
				modalId={modalId}
				className="history-refund refund-modal"
				header="Refund"
				primaryButton={buttons.primaryButton}
				secondaryButton={buttons.secondaryButton}
				size="large"
			>
				{refundModalView === RefundModalView.NetStamps && (
					<NetStampsRefundContent
						selectedPrintsForRefund={selectedPrintsForRefund}
						mailInPrints={mailInPrints}
						totalCost={totalCost}
						onSelectionChanged={this.onNetStampsRefundSelectionChanged}
					/>
				)}
				{isLoading && <i className="loader-icon" />}
				{refundModalView === RefundModalView.Certified && (
					<CertifiedRefundContent
						selectedPrintsForRefund={selectedPrintsForRefund}
					/>
				)}
			</Modal>
		);
	}
}

RefundModal.propTypes = {
	data: PropTypes.shape({
		eligiblePrints: PropTypes.array,
	}),
};

export default withLayoutAndModalContexts(RefundModal);
